import React, { useState, useMemo } from "react";
import {
  Button,
  Container,
  Paper,
  Grid,
  Typography,
  Alert,
  Box,
  Stack,
  Fade,
  Divider,
  Tooltip
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

// Images
import visioConf from "../../../../../Assets/images/consultaion-visio-matching-platform.png";
import chat from "../../../../../Assets/images/consultaion-chat-matching-platform.png";
import appelTel from "../../../../../Assets/images/consultaion-audio-matching-platform.png";
import emailing from "../../../../../Assets/images/emailing-matching-platform.png";

import "react-toastify/dist/ReactToastify.css";
import InfosModal from "./InfosModel/InfosModal";

// --- Animations ---
const pulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
  100% { opacity: 1; transform: scale(1); }
`;

// --- Styled Components Expert ---

const FilterButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  borderRadius: "100px",
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.85rem",
  transition: "all 0.3s ease",
  backgroundColor: active ? "#4AAE9B" : "#FFFFFF",
  color: active ? "#fff" : "#64748B",
  border: "1px solid",
  borderColor: active ? "#4AAE9B" : "#E2E8F0",
  boxShadow: active ? "0 4px 12px rgba(74, 174, 155, 0.2)" : "none",
  "&:hover": {
    backgroundColor: active ? "#3d9685" : "#F8FAFC",
    borderColor: "#4AAE9B",
  },
}));

const ExpertCard = styled(Paper)(({ theme }) => ({
  borderRadius: "24px",
  border: "1px solid #F1F5F9",
  backgroundColor: "#FFFFFF",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  cursor: "pointer",
  position: "relative",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.08)",
    borderColor: "#4AAE9B",
  },
}));

const ImageStage = styled(Box)({
  height: "160px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#F8FAFC",
  position: "relative",
});

const MobilityBadge = styled(Box)({
  position: "absolute",
  top: 16,
  left: 16,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#FFFBEB",
  borderRadius: "50%",
  padding: "8px",
  color: "#D97706",
  animation: `${pulse} 2s infinite ease-in-out`,
  boxShadow: "0 2px 8px rgba(217, 119, 6, 0.2)",
});

const PriceBadge = styled(Box)({
  position: "absolute",
  top: 16,
  right: 16,
  backgroundColor: "#FFFFFF",
  padding: "4px 10px",
  borderRadius: "8px",
  fontWeight: 800,
  color: "#1E293B",
  fontSize: "0.85rem",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
});

const DescriptionTruncated = styled(Typography)({
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: "#64748B",
  fontSize: "0.875rem",
  lineHeight: 1.6,
  minHeight: "2.8rem",
});

const typeOffreImages = {
  chat,
  call: appelTel,
  visio_conference: visioConf,
  emailing,
};

function Offres({ userData, Categories }) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedOffre, setSelectedOffre] = useState(null);
  const [openInfosModal, setOpenInfosModal] = useState(false);

  const handleClickOpenInfosModal = (offre) => {
    setSelectedOffre(offre);
    setOpenInfosModal(true);
  };

  // 1. Filtrage des offres actives
  const offres = useMemo(() => {
    return (userData?.info_offres || []).filter(
      (o) => o.status === 1 && o.available_slots?.length > 0
    );
  }, [userData.info_offres]);

  // 2. Identification des catégories qui ont réellement des offres
  const categoriesWithOffers = useMemo(() => {
    const categoryIds = new Set(offres.map((o) => o.id_category || o?.offre?.category?.id_category));
    return Categories.filter((cat) => categoryIds.has(cat.id_category));
  }, [Categories, offres]);

  // 3. Application du filtre sélectionné
  const filteredOffers = useMemo(() => {
    return offres.filter((offer) => {
      const categoryId = offer.id_category || offer?.offre?.category?.id_category || null;
      return selectedCategory === null || categoryId === selectedCategory;
    });
  }, [offres, selectedCategory]);

  if (offres.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Alert severity="info" variant="outlined" sx={{ borderRadius: "16px", display: "inline-flex" }}>
          {t("offres.noOffersAvailable")}
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {selectedOffre && (
        <InfosModal isOpen={openInfosModal} setOpenInfosModal={setOpenInfosModal} offre={selectedOffre} />
      )}

      {/* Barre de Catégories */}
      <Box sx={{ mb: 6, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: "#0F172A", mb: 4 }}>
          {t("offres.introText")}
        </Typography>

        <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" sx={{ gap: 1.5 }}>
          <FilterButton 
            active={selectedCategory === null} 
            onClick={() => setSelectedCategory(null)}
          >
            {t("offres.all")}
          </FilterButton>
          {categoriesWithOffers.map((category) => (
            <FilterButton
              key={category.id_category}
              active={selectedCategory === category.id_category}
              onClick={() => setSelectedCategory(category.id_category)}
            >
              {category.title_category}
            </FilterButton>
          ))}
        </Stack>
      </Box>

      {/* Grid d'offres */}
      <Grid container spacing={3}>
        {filteredOffers.length === 0 ? (
           <Grid item xs={12}>
             <Alert severity="info" sx={{ borderRadius: "12px" }}>{t("offres.noOffersInCategory")}</Alert>
           </Grid>
        ) : (
          filteredOffers.map((offre, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Fade in timeout={300}>
                <ExpertCard elevation={0} onClick={() => handleClickOpenInfosModal(offre)}>
                  <ImageStage>
                    {/* Icône clignotante si déplacement possible */}
                    {offre.is_deplacement_possible === 1 && (
                      <Tooltip title="Déplacement possible" arrow placement="top">
                        <MobilityBadge>
                          <DirectionsCarIcon sx={{ fontSize: "1.2rem" }} />
                        </MobilityBadge>
                      </Tooltip>
                    )}

                    <PriceBadge>{Math.round(offre.prix_offre)}€</PriceBadge>
                    <img
                      src={typeOffreImages[offre.type_offre]}
                      alt={offre.type_offre}
                      style={{ maxHeight: "100px", width: "auto" }}
                    />
                  </ImageStage>

                  <Box sx={{ p: 3, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", mb: 1, fontSize: "1.05rem", minHeight: "2.7rem" }}>
                      {offre.titre_offre}
                    </Typography>

                    <DescriptionTruncated sx={{ mb: 2 }}>
                      {offre.description_offre}
                    </DescriptionTruncated>

                    <Divider sx={{ my: 2, borderColor: "#F1F5F9" }} />

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: "auto" }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AccessTimeIcon sx={{ fontSize: "1rem", color: "#94A3B8" }} />
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#64748B" }}>
                          {offre.duree_offre} min
                        </Typography>
                      </Stack>

                      <Button
                        variant="contained"
                        sx={{
                          bgcolor: "#4AAE9B",
                          color: "#fff",
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: "10px",
                          px: 3,
                          boxShadow: "none",
                          "&:hover": { bgcolor: "#3d9685", boxShadow: "none" }
                        }}
                      >
                        {t("offres.reserve")}
                      </Button>
                    </Stack>
                  </Box>
                </ExpertCard>
              </Fade>
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
}

export default Offres;